// src/components/Doctors/DoctorCard.js
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "../button";
import { Badge } from "../badge";
import {
  Stethoscope,
  MapPin,
  Clock,
  Star,
  Phone,
  Award,
  UserPlus,
} from "lucide-react";
import { generateWhatsAppLink } from "@/mock/mockData";

const DoctorCard = ({ doctor }) => {
  const { isAuthenticated, user } = useAuth();
  const [imageError, setImageError] = useState(false);

  // Default placeholder image - use a local asset to avoid multiple network calls
  const defaultDoctorImage = "/doctor-placeholder.jpg";

  // Map API properties to component properties
  const doctorWithData = {
    // Direct API properties
    name: doctor.name,
    specialty: doctor.specialty,
    apmc_number: doctor.apmc_number,
    degrees: doctor.degrees,
    occupation: doctor.occupation,
    photo_url: doctor.photo_url,
    clinic_id: doctor.clinic_id,
    clinic_name: doctor.clinic_name,
    id: doctor.id,

    // Enhanced with mock data for missing fields
    photo:
      doctor.photo_url && !imageError ? doctor.photo_url : defaultDoctorImage,
    experience: doctor.experience || "10+ years",
    rating: doctor.rating || 4.5,
    distance: doctor.distance || "2.5 km",
    clinic: doctor.clinic_name || doctor.occupation || "City Clinic",
    address:
      doctor.address ||
      `${doctor.clinic_name || doctor.occupation}, ${
        doctor.city || "Your City"
      }`,
    phone: doctor.phone || "+91 XXXXXXXXXX",
    achievements:
      doctor.achievements ||
      (doctor.degrees
        ? doctor.degrees.split(", ")
        : ["MBBS", "MD", "Senior Consultant"]),
    isAvailableToday:
      doctor.isAvailableToday !== undefined ? doctor.isAvailableToday : true,
    nextAvailableSlot: doctor.nextAvailableSlot || "2:30 PM",
    availableSlots: doctor.availableSlots || ["10:00 AM", "2:30 PM", "5:00 PM"],
  };

  const handleImageError = () => {
    setImageError(true);
  };

  // Smart availability: compare available slots against current time
  const getAvailabilityInfo = () => {
    const now = new Date();
    const slots = doctorWithData.availableSlots;

    // Parse a time string like "2:30 PM" into today's Date object
    const parseSlotTime = (timeStr) => {
      const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (!match) return null;
      let hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      const period = match[3].toUpperCase();
      if (period === "PM" && hours !== 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;
      const d = new Date(now);
      d.setHours(hours, minutes, 0, 0);
      return d;
    };

    // Find the next slot that is still in the future
    const upcomingSlots = slots
      .map((slot) => ({ label: slot, time: parseSlotTime(slot) }))
      .filter((s) => s.time && s.time > now)
      .sort((a, b) => a.time - b.time);

    if (upcomingSlots.length > 0) {
      // There's still a slot available today
      return {
        isToday: true,
        label: "Available Today",
        nextSlot: upcomingSlots[0].label,
        color: "green",
      };
    }

    // All slots have passed — show earliest slot for tomorrow
    const allSlotsSorted = slots
      .map((slot) => ({ label: slot, time: parseSlotTime(slot) }))
      .filter((s) => s.time)
      .sort((a, b) => a.time - b.time);

    const earliestSlot =
      allSlotsSorted.length > 0
        ? allSlotsSorted[0].label
        : doctorWithData.nextAvailableSlot;

    return {
      isToday: false,
      label: "Available Tomorrow",
      nextSlot: earliestSlot,
      color: "orange",
    };
  };

  const availability = getAvailabilityInfo();

  const handleBookAppointment = () => {
    if (!isAuthenticated) {
      window.location.href = "/auth";
      return;
    }

    const whatsAppLink = generateWhatsAppLink("doctor", {
      user: user,
      specialty: doctorWithData.specialty,
      doctorName: doctorWithData.name,
      preferredTime: "Today evening",
      location: doctorWithData.address,
    });
    window.open(whatsAppLink, "_blank");
  };

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-orange-100 overflow-hidden">
      <div className="p-6">
        <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-4">
          {/* Doctor Photo with optimized loading */}
          <div className="relative flex-shrink-0">
            <img
              src={doctorWithData.photo}
              alt={doctorWithData.name}
              className="w-24 h-24 rounded-full object-cover border-4 border-orange-100"
              loading="lazy" // Add lazy loading
              onError={handleImageError}
            />
            <div className="absolute -bottom-2 -right-2 bg-green-500 w-6 h-6 rounded-full border-2 border-white flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
          </div>

          {/* Doctor Info */}
          <div className="flex-1 min-w-0">
            {" "}
            {/* Added min-w-0 for text truncation */}
            <div className="flex items-start justify-between mb-2">
              <div className="min-w-0 flex-1 mr-4">
                {" "}
                {/* Added for text overflow */}
                <h3 className="text-xl font-semibold text-gray-900 break-words">
                  {doctorWithData.name}
                </h3>
                <p className="text-orange-600 font-medium break-words">
                  {doctorWithData.specialty}
                </p>
                {doctorWithData.apmc_number && (
                  <p className="text-sm text-gray-500">
                    APMC: {doctorWithData.apmc_number}
                  </p>
                )}
                {doctorWithData.degrees && (
                  <p className="text-sm text-gray-500 break-words">
                    {doctorWithData.degrees}
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-1 flex-shrink-0">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span className="font-medium">{doctorWithData.rating}</span>
              </div>
            </div>
            <div className="space-y-2 mb-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Stethoscope className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">
                  {doctorWithData.experience} experience
                </span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">
                  {doctorWithData.occupation || doctorWithData.clinic}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span>{doctorWithData.distance} away</span>
              </div>
            </div>
            {/* Achievements */}
            {doctorWithData.achievements &&
              doctorWithData.achievements.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {doctorWithData.achievements
                    .slice(0, 2)
                    .map((achievement, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="text-xs border-orange-200 text-orange-700 max-w-full truncate"
                      >
                        <Award className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{achievement}</span>
                      </Badge>
                    ))}
                </div>
              )}
            {/* Availability */}
            {doctorWithData.isAvailableToday && (
              <div className="flex items-center space-x-2 mb-4">
                <Clock className={`w-4 h-4 flex-shrink-0 ${
                  availability.color === "green" ? "text-green-600" : "text-orange-600"
                }`} />
                <span className={`text-sm font-medium ${
                  availability.color === "green" ? "text-green-600" : "text-orange-600"
                }`}>
                  {availability.label}
                </span>
                <Badge className={`text-xs ${
                  availability.color === "green"
                    ? "bg-green-100 text-green-700"
                    : "bg-orange-100 text-orange-700"
                }`}>
                  Next: {availability.nextSlot}
                </Badge>
              </div>
            )}
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              {isAuthenticated ? (
                <Button
                  onClick={handleBookAppointment}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 w-full sm:w-auto"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Book on WhatsApp
                </Button>
              ) : (
                <Link to="/auth" className="flex-1">
                  <Button className="w-full bg-orange-600 hover:bg-orange-700">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Sign Up for Free to Book
                  </Button>
                </Link>
              )}
              <Button
                variant="outline"
                className="border-orange-200 text-orange-600 w-full sm:w-auto"
              >
                View Profile
              </Button>
            </div>
            {/* Phone Visible */}
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-sm text-gray-600">
                Call directly:{" "}
                <span className="font-medium text-gray-900">
                  {doctorWithData.phone}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorCard;
